import React, { useState, useEffect } from "react";
import { X, Heart, Calendar, Sparkles, Video, Mic, MessageSquare, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Portal, PortalCapsule, PortalMessage } from "@/hooks/usePortal";
import { useAuthContext } from "@/contexts/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

interface PortalMemoryGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  portalId: string;
  portal: Portal;
  capsules: PortalCapsule[];
  messages: PortalMessage[];
}

interface PortalMemory {
  id: string;
  type: "capsule" | "message" | "echo" | "milestone";
  content?: string;
  mediaUrl?: string;
  timestamp: string;
  isOwn: boolean;
}

const PortalMemoryGallery: React.FC<PortalMemoryGalleryProps> = ({
  isOpen,
  onClose,
  portalId,
  portal,
  capsules,
  messages,
}) => {
  const [memories, setMemories] = useState<PortalMemory[]>([]);
  const { user } = useAuthContext();

  useEffect(() => {
    if (!isOpen) return;

    const memoryList: PortalMemory[] = [];

    // Add capsules (including echo capsules)
    capsules.forEach((capsule) => {
      memoryList.push({
        id: capsule.id,
        type: (capsule as any).is_echo ? "echo" : "capsule", // Check if it's an echo capsule
        content: capsule.prompt_text || undefined,
        mediaUrl: capsule.content_url || undefined,
        timestamp: capsule.created_at,
        isOwn: capsule.sender_user_id === user?.id,
      });
    });

    // Add messages
    messages.forEach((msg) => {
      memoryList.push({
        id: msg.id,
        type: "message",
        content: msg.text,
        timestamp: msg.created_at,
        isOwn: msg.sender_user_id === user?.id,
      });
    });

    // Add milestones
    if (portal.chat_unlocked) {
      memoryList.push({
        id: `milestone-unlock-${portal.id}`,
        type: "milestone",
        content: "Portal Resonance — Chat unlocked",
        timestamp: portal.updated_at,
        isOwn: false,
      });
    }

    // Sort by timestamp
    memoryList.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    setMemories(memoryList);
  }, [isOpen, capsules, messages, portal, user?.id]);

  // Group by date
  const groupedMemories = memories.reduce((acc, memory) => {
    const date = new Date(memory.timestamp).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(memory);
    return acc;
  }, {} as Record<string, PortalMemory[]>);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-primary/5 to-secondary/5">
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Heart className="w-6 h-6 text-primary" />
              Portal Memory Gallery
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Your shared timeline with {portal.other_user?.name}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 p-6">
          {memories.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-16 h-16 text-primary/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No memories yet</h3>
              <p className="text-sm text-muted-foreground">
                Your Portal timeline will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedMemories).map(([date, dateMemories]) => (
                <div key={date} className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    {date}
                  </div>
                  <div className="space-y-3">
                    {dateMemories.map((memory) => (
                      <div
                        key={memory.id}
                        className={cn(
                          "p-4 rounded-xl border",
                          memory.isOwn
                            ? "bg-primary/5 border-primary/20 ml-auto max-w-[80%]"
                            : "bg-muted/50 border-border max-w-[80%]"
                        )}
                      >
                        <div className="flex items-start gap-2 mb-2">
                          {memory.type === "capsule" && <MessageSquare className="w-4 h-4 text-primary" />}
                          {memory.type === "echo" && <Sparkles className="w-4 h-4 text-purple-500" />}
                          {memory.type === "message" && <MessageSquare className="w-4 h-4 text-blue-500" />}
                          {memory.type === "milestone" && <Sparkles className="w-4 h-4 text-green-500" />}
                          <span className="text-xs font-medium capitalize">
                            {memory.type === "echo" ? "Echo Capsule" : memory.type}
                          </span>
                        </div>

                        {memory.content && (
                          <p className="text-sm text-foreground mb-2">{memory.content}</p>
                        )}

                        {memory.mediaUrl && (
                          <div className="mb-2">
                            {memory.mediaUrl.includes("video") ? (
                              <video src={memory.mediaUrl} controls className="rounded-lg max-w-full" />
                            ) : memory.mediaUrl.includes("audio") ? (
                              <audio src={memory.mediaUrl} controls className="w-full" />
                            ) : (
                              <img src={memory.mediaUrl} alt="Memory" className="rounded-lg max-w-full" />
                            )}
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(memory.timestamp), { addSuffix: true })}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

export default PortalMemoryGallery;

